/* eslint-disable no-lonely-if */
import React, { useContext, memo, useMemo, useEffect } from 'react'
import { Button, Space, Card, Flex, ColorPicker, Collapse, Form, Typography } from 'antd'
import { CloseCircleOutlined, DeleteOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons'
import { t } from 'i18next'
import classNames from 'classnames'
import { useForm } from 'antd/es/form/Form'
import { ReportGrammarRules } from '../../../../../../../redux/config/customReport/types'
import { VisitorContext, State, ParameterMapping, NormalizedBuilderNode, ErrorMap } from '../types'
import StyledTreeBranch from './styled/StyledTreeBranch'
import StyledTreeNode from './styled/StyledTreeNode'
import CompanyNodeContent from './nodes/CompanyNodeContent'
import DimensionNodeContent from './nodes/DimensionNodeContent'
import PeriodGroupNodeContent from './nodes/PeriodGroupNodeContent'
import FunctionNodeContent from './nodes/FunctionNodeContent'
import ReferenceNodeContent from './nodes/ReferenceNodeContent'
import KeyFigureNodeContent from './nodes/KeyFigureNodeContent'
import FormulaNodeContent from './nodes/FormulaNodeContent'
import RowSettings from './CategorySettingsModal'
import { TreeContext } from '../CategoryBuilder'
import {
  CustomReportCategoryType,
  CustomReportCategory,
  FunctionCategoryValue
} from '../../../../../../../redux/context/customReports/typesCategory'
import ToggleButton from './ToggleButton'
import { FormItemGroup } from '../../../../../../../components/Form/FormItemContext'
import SeriesStyleFields from '../../../chart/SeriesStyleFields'
import { CustomReportSectionsType } from '../../../../../../../redux/context/customReports/typesSection'
import { useDeleteConfirmModal } from '../../../../../../../shared/modals'

const { Text } = Typography

interface TreeNodeProps {
  nodeId: string
  nodes: Record<string, NormalizedBuilderNode>
  rules: ReportGrammarRules
  dropIndicator: { nodeId: string | null; position: 'before' | 'after' | 'inside' | null }
  colorPrimary: string
  rootId: string // Juurisolmun ID
  switchRowsAndColumns: boolean
  sectionType: CustomReportSectionsType['type']
  errors?: ErrorMap
  onToggle: (id: string) => void
  onRemove: (id: string) => void
  onAddChild: (parentId: string, type: CustomReportCategoryType, context: VisitorContext) => void
  onUpdateValues: (id: string, values: Partial<CustomReportCategory>) => void
  onDragStart: (nodeId: string) => void
  onDragEnd: () => void
  onDrop: (targetNodeId: string, position: 'before' | 'after' | 'inside') => void
  onDropIndicator: (nodeId: string | null, position: 'before' | 'after' | 'inside' | null) => void
}

// Helper function to determine if a node is a leaf
const isNodeLeaf = (node: NormalizedBuilderNode, context: VisitorContext): boolean => {
  if (context.isFunctionDecendant) {
    return false
  }

  if (node.type === 'function' || node.type === 'periodGroup') {
    return true
  }

  return false
}

// Helper function to get available child types
const getAvailableChildTypes = (
  node: NormalizedBuilderNode,
  context: VisitorContext,
  rules: ReportGrammarRules
): CustomReportCategoryType[] => {
  if (rules.terminals.includes(node.type)) return []

  const { state } = context

  // If the node is a function, handle differently
  if (node.type === 'function') {
    const val = node.value as FunctionCategoryValue
    if (val) {
      const { mapping, max, types } = rules.functions[val] || {}

      if (mapping === ParameterMapping.Agnostic) {
        if (node.childIds?.length < (max || Infinity)) {
          return types?.[state]?.[0] || []
        }
        return []
      }

      return types?.[state]?.[node.childIds?.length || 0] || []
    }
    return []
  }

  // Otherwise use transitions from the grammar
  if (state && rules.transitions[state]) {
    return rules.transitions[state] || []
  }

  return []
}

// Helper function to determine if node can be deleted
const canDeleteNode = (
  nodeId: string,
  nodes: Record<string, NormalizedBuilderNode>,
  rules: ReportGrammarRules
): boolean => {
  // Find parent node
  const parentId = Object.keys(nodes).find(id => nodes[id].childIds.includes(nodeId))
  if (!parentId) return true // No parent, can delete

  const parent = nodes[parentId]

  // Check if parent is a function with specific mapping
  if (parent.type === 'function') {
    const value = parent.value as FunctionCategoryValue
    if (value && rules.functions[value]?.mapping === ParameterMapping.Specific) {
      // Can only delete if it's the last child
      const lastChildId = parent.childIds[parent.childIds.length - 1]
      return nodeId === lastChildId
    }
  }

  return true // Default: can delete
}

// Main TreeNode component - memoized for performance
const TreeNode: React.FC<TreeNodeProps> = memo(
  ({
    nodeId,
    nodes,
    rules,
    dropIndicator,
    colorPrimary,
    rootId,
    switchRowsAndColumns,
    sectionType,
    errors = {},
    onToggle,
    onRemove,
    onAddChild,
    onUpdateValues,
    onDragStart,
    onDragEnd,
    onDrop,
    onDropIndicator
  }) => {
    const [styleForm] = useForm()
    const node = nodes[nodeId]
    const parentContext = useContext(TreeContext)
    const deleteConfirmModal = useDeleteConfirmModal()

    const categoryStylesEnabled = useMemo(
      () => switchRowsAndColumns && sectionType === 'graph',
      [switchRowsAndColumns, sectionType]
    )

    // Initialize the form with node.style when the component mounts or when node.style changes
    useEffect(() => {
      if (categoryStylesEnabled && node && node.style) {
        styleForm.setFieldsValue({ style: node.style })
      }
    }, [node, node?.style, categoryStylesEnabled, styleForm])

    if (!node) return null

    const nodeErrors = errors[nodeId] || []
    const hasErrors = nodeErrors.length > 0

    // Determine the appropriate context for this node
    const getNodeContext = (): VisitorContext => {
      const baseContext = { ...parentContext }

      // Update context based on node type
      switch (node.type) {
        case 'root':
          return { ...baseContext, state: State.Root }
        case 'company':
          return { ...baseContext, company: node.value as string, state: State.Company }
        case 'dimension':
          return { ...baseContext, dimension: node.value as string, state: State.Dimension }
        case 'periodGroup':
          return { ...baseContext, state: State.PeriodGroup }
        case 'function':
          return { ...baseContext }
        default:
          return baseContext
      }
    }

    const context = getNodeContext()

    // Lisätään TreeNode komponenttiin uusi funktio tason määrittämiseksi
    const getNodeLevel = (dragNodeId: string, allNodes: Record<string, NormalizedBuilderNode>): number => {
      // Juurisolmun taso on 0
      if (allNodes[dragNodeId].type === 'root') return 0

      // Etsitään solmun vanhempi
      const parentId = Object.keys(allNodes).find(id => allNodes[id].childIds.includes(dragNodeId))
      if (!parentId) return 1 // Jos vanhempaa ei löydy, oletetaan tason 1

      // Vanhemman taso + 1
      return getNodeLevel(parentId, allNodes) + 1
    }

    const handleDragStart = (e: React.DragEvent) => {
      // Estetään function-jälkeläisten raahaaminen
      if (context.isFunctionDecendant) {
        e.preventDefault()
        e.stopPropagation()
        return
      }

      e.dataTransfer.setData('text/plain', nodeId)
      e.dataTransfer.effectAllowed = 'move'

      // Asetetaan draggausoperaatio
      setTimeout(() => {
        onDragStart(nodeId)
      }, 0)
    }

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault()

      // Jos raahattava node on null, perutaan
      if (!parentContext.draggedNodeId) return

      // Haetaan raahattavan noden taso
      const sourceNodeId = parentContext.draggedNodeId
      const sourceLevel = getNodeLevel(sourceNodeId, nodes)
      const targetLevel = getNodeLevel(nodeId, nodes)

      // Jos kyseessä on eri taso, ei sallita pudotusta
      if (sourceLevel !== targetLevel) {
        e.dataTransfer.dropEffect = 'none'
        onDropIndicator(null, null)
        return
      }

      e.dataTransfer.dropEffect = 'move'

      // Calculate drop position
      const rect = e.currentTarget.getBoundingClientRect()
      const y = e.clientY - rect.top
      const { height } = rect

      // Yksinkertaistettu: rajoitetaan vain before/after, ei inside
      const position = y < height * 0.5 ? 'before' : 'after'
      onDropIndicator(nodeId, position)
    }

    const handleDragLeave = () => {
      onDropIndicator(null, null)
    }

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault()

      // Get the current drop position from the indicator
      if (dropIndicator.nodeId === nodeId && dropIndicator.position) {
        // Varmistetaan vielä pudotuksen yhteydessä, että taso täsmää
        const sourceNodeId = parentContext.draggedNodeId
        if (!sourceNodeId) return

        const sourceLevel = getNodeLevel(sourceNodeId, nodes)
        const targetLevel = getNodeLevel(nodeId, nodes)

        // Jos tasot eivät täsmää, ei sallita pudotusta
        if (sourceLevel !== targetLevel) {
          onDropIndicator(null, null)
          return
        }

        onDrop(nodeId, dropIndicator.position)
      }

      onDropIndicator(null, null)
    }

    // Helper to render node content by type
    const renderNodeContent = () => {
      switch (node.type) {
        case 'company':
          return (
            <CompanyNodeContent
              nodesById={nodes}
              node={node}
              rules={rules}
              context={context}
              onUpdateValue={onUpdateValues}
            />
          )
        case 'dimension':
          return <DimensionNodeContent node={node} rules={rules} context={context} onUpdateValue={onUpdateValues} />
        case 'periodGroup':
          return <PeriodGroupNodeContent node={node} rules={rules} context={context} onUpdateValue={onUpdateValues} />
        case 'function':
          return (
            <FunctionNodeContent
              nodesById={nodes}
              node={node}
              rules={rules}
              context={context}
              onUpdateValue={onUpdateValues}
            />
          )
        case 'reference':
          return (
            <ReferenceNodeContent
              nodesById={nodes}
              rootId={rootId} // Välitetään juurisolmun ID
              node={node}
              rules={rules}
              context={context}
              onUpdateValue={onUpdateValues}
            />
          )
        case 'keyFigureId':
          return <KeyFigureNodeContent node={node} rules={rules} context={context} onUpdateValue={onUpdateValues} />
        case 'formulaId':
          return <FormulaNodeContent node={node} rules={rules} context={context} onUpdateValue={onUpdateValues} />
        default:
          return null
      }
    }

    // Determine if this is a leaf node
    const isLeaf = isNodeLeaf(node, context)

    // Get available child types for this node
    const childTypes = getAvailableChildTypes(node, context, rules)

    // Check if this node is a drop target
    const showBefore = dropIndicator.nodeId === nodeId && dropIndicator.position === 'before'
    const showAfter = dropIndicator.nodeId === nodeId && dropIndicator.position === 'after'
    const showInside = dropIndicator.nodeId === nodeId && dropIndicator.position === 'inside'

    // Root node has special rendering
    if (node.type === ('root' as string)) {
      return (
        <div data-cy="tree-root-node">
          <Card
            size="small"
            style={{
              marginBottom: 4,
              backgroundColor: showInside ? '#50dfff' : ''
            }}
            styles={{
              body: {
                padding: 4
              }
            }}
            title={t('categoryBuilder:rootLevel')}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-cy="tree-root-card"
          >
            <Flex justify="space-between">
              <Space>
                {childTypes.length > 0 &&
                  childTypes.map(type => (
                    <Button
                      key={type}
                      icon={<PlusOutlined />}
                      onClick={() => onAddChild(nodeId, type, context)}
                      data-cy={`add-${type}-button`}
                    >
                      {t(`customReportCategoryType:${type}`)}
                    </Button>
                  ))}
              </Space>
              {hasErrors && (
                <Text type="danger">
                  {nodeErrors.map((error, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={idx}>
                      <CloseCircleOutlined /> {t(error.message, error.messageParams)}
                    </div>
                  ))}
                </Text>
              )}
            </Flex>
          </Card>

          {node.expanded && node.childIds.length > 0 && (
            <div data-cy="root-children">
              {node.childIds.map(childId => (
                <TreeNode
                  key={childId}
                  nodeId={childId}
                  nodes={nodes}
                  rules={rules}
                  dropIndicator={dropIndicator}
                  colorPrimary={colorPrimary}
                  rootId={rootId}
                  switchRowsAndColumns={switchRowsAndColumns}
                  sectionType={sectionType}
                  errors={errors}
                  onToggle={onToggle}
                  onRemove={onRemove}
                  onAddChild={onAddChild}
                  onUpdateValues={onUpdateValues}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onDrop={onDrop}
                  onDropIndicator={onDropIndicator}
                />
              ))}
            </div>
          )}
        </div>
      )
    }

    // Standard node rendering for all other types
    return (
      <StyledTreeBranch before={showBefore} after={showAfter} data-cy={`tree-branch-${node.type}-${nodeId}`}>
        {node.childIds.length > 0 && <ToggleButton node={node} nodeId={node.id} onToggle={onToggle} />}

        <StyledTreeNode
          colorPrimary={colorPrimary}
          className={classNames('tree-node', node.type, {
            'opacity-10': node.isDragging,
            'non-draggable': context.isFunctionDecendant,
            leaf: isLeaf,
            error: hasErrors
          })}
          style={{
            marginBottom: 4,
            backgroundColor: showInside && node.type !== 'function' ? '#50dfff' : '',
            cursor: node.type === 'function' && !isLeaf ? 'no-drop' : undefined
          }}
          styles={{
            body: {
              padding: 4
            }
          }}
          draggable={node.type !== 'root' && !context.isFunctionDecendant}
          onDragStart={handleDragStart}
          onDragEnd={onDragEnd}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          data-cy={`tree-node-${node.type}-${nodeId}`}
        >
          <Flex justify="space-between">
            <div>
              <Space>
                <span data-cy={`node-type-label-${node.type}`}>{t(`customReportCategoryType:${node.type}`)}:</span>
                <div data-cy={`node-content-${node.type}`}>{renderNodeContent()}</div>
                {/* Child type buttons */}
                {childTypes?.map(type => (
                  <Button
                    key={type}
                    icon={<PlusOutlined />}
                    onClick={() => onAddChild(nodeId, type, context)}
                    data-cy={`add-child-${type}-button-${nodeId}`}
                  >
                    {t(`customReportCategoryType:${type}`)}
                  </Button>
                ))}
              </Space>
            </div>

            <Space>
              {hasErrors && (
                <Text type="danger">
                  {nodeErrors.map((error, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={idx}>
                      <CloseCircleOutlined /> {t(error.message, error.messageParams)}
                    </div>
                  ))}
                </Text>
              )}
              {isLeaf ? (
                <ColorPicker
                  allowClear
                  value={node.color}
                  onChange={value => {
                    onUpdateValues(nodeId, { color: value.cleared ? undefined : value.toHexString() })
                  }}
                  data-cy={`color-picker-${nodeId}`}
                />
              ) : null}
              {node.type !== 'reference' ? (
                <RowSettings
                  node={node}
                  rules={rules}
                  context={context}
                  onUpdateValue={onUpdateValues}
                  nodesById={nodes}
                />
              ) : null}
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={!canDeleteNode(nodeId, nodes, rules)}
                onClick={() => {
                  if (canDeleteNode(nodeId, nodes, rules)) {
                    deleteConfirmModal({ onOk: () => onRemove(nodeId) })
                  }
                }}
                title={
                  !canDeleteNode(nodeId, nodes, rules)
                    ? 'Only the last child can be deleted for functions with specific parameter mapping'
                    : ''
                }
                data-cy={`delete-node-${nodeId}`}
              />
            </Space>
            {/* Error display */}
          </Flex>
          {categoryStylesEnabled && isLeaf ? (
            <Collapse
              bordered={false}
              size="small"
              expandIcon={({ isActive }) => <SettingOutlined rotate={isActive ? 90 : 0} />}
              style={{
                // background: token.colorBgContainer,
                margin: 0,
                marginTop: 4
              }}
              items={[
                {
                  key: '1',
                  label: t('global:style'),
                  children: (
                    <Space>
                      <Form
                        // key={path}
                        layout="inline"
                        form={styleForm}
                        onValuesChange={(val, allVals) => onUpdateValues(node.id, allVals)}
                      >
                        <FormItemGroup prefix={['style']}>
                          <SeriesStyleFields />
                        </FormItemGroup>
                      </Form>
                    </Space>
                  )
                }
              ]}
            />
          ) : null}
        </StyledTreeNode>

        {/* Render children if expanded */}
        {node.expanded && node.childIds.length > 0 && (
          <div className="" data-cy={`node-children-${nodeId}`}>
            <TreeContext.Provider
              value={node.type === 'function' ? { ...context, isFunctionDecendant: true } : context}
            >
              {node.childIds.map(childId => (
                <TreeNode
                  key={childId}
                  nodeId={childId}
                  nodes={nodes}
                  rules={rules}
                  dropIndicator={dropIndicator}
                  colorPrimary={colorPrimary}
                  rootId={rootId}
                  errors={errors}
                  switchRowsAndColumns={switchRowsAndColumns}
                  sectionType={sectionType}
                  onToggle={onToggle}
                  onRemove={onRemove}
                  onAddChild={onAddChild}
                  onUpdateValues={onUpdateValues}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onDrop={onDrop}
                  onDropIndicator={onDropIndicator}
                />
              ))}
            </TreeContext.Provider>
          </div>
        )}
      </StyledTreeBranch>
    )
  },
  (prevProps, nextProps) => {
    // Aina renderöi uudelleen, jos nodeId on muuttunut
    return false
    if (prevProps.nodeId !== nextProps.nodeId) return false

    const prevNode = prevProps.nodes[prevProps.nodeId]
    const nextNode = nextProps.nodes[nextProps.nodeId]

    // console.log(prevNode, nextNode)

    // Aina renderöi, jos solmu ei ole olemassa kummassakaan tilassa
    if (!prevNode || !nextNode) return false

    // Tarkista, onko tämä solmu lisätty juuri äskettäin
    // Tämä on kriittinen korjaus - vertaa kaikkien saatavilla olevien solmujen määrää
    // Sen sijaan, että tarkistettaisiin vain childIds.length
    const prevNodeKeys = Object.keys(prevProps.nodes)
    const nextNodeKeys = Object.keys(nextProps.nodes)
    if (prevNodeKeys.length !== nextNodeKeys.length) return false

    // Tarkista onko solmu tai jokin sen lapsista muuttunut
    const nodeChanged =
      prevNode.expanded !== nextNode.expanded ||
      prevNode.isDragging !== nextNode.isDragging ||
      prevNode.color !== nextNode.color ||
      prevNode.value !== nextNode.value ||
      // Tarkista lasten määrä
      prevNode.childIds.length !== nextNode.childIds.length

    // Jos solmu on muuttunut, renderöi
    if (nodeChanged) return false

    // Tarkista myös, ovatko lapset muuttuneet (eri järjestys tai eri solmut)
    if (prevNode.childIds.length > 0) {
      // Tarkista onko childIds sisältö muuttunut
      const childIdsChanged = !prevNode.childIds.every((id, index) => id === nextNode.childIds[index])
      if (childIdsChanged) return false

      // Tarkista myös, onko yksikään lapsista muuttunut sisällöltään
      for (const childId of prevNode.childIds) {
        if (!nextProps.nodes[childId] || prevProps.nodes[childId] !== nextProps.nodes[childId]) {
          return false
        }
      }
    }

    // Tarkista, vaikuttaako drop indicator tähän solmuun
    const indicatorAffectsThisNode =
      (prevProps.dropIndicator.nodeId === prevProps.nodeId) !== (nextProps.dropIndicator.nodeId === nextProps.nodeId)

    if (indicatorAffectsThisNode) return false

    // Palauta true vain jos mikään ei ole muuttunut (ei uudelleenrenderöintiä)
    return true
  }
)

export default TreeNode
